import "./style.scss";

import { ApartmentOutlined } from "@mui/icons-material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { Tooltip } from "@mui/material";
import { useBPCompanySettings } from "hooks/useBPCompanySettings";
import { useIsMobile } from "hooks/useIsMobile";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import type { ICustomField } from "store/customFieldsStore";
import { toggleSettingsTab } from "store/settingsStore";
import type { RootState } from "store/store";
import { useAppDispatch } from "store/store";

import MissingInfosWarning from "../BPPanelSettings/MissingInfosWarning";
import { getMissingFieldsNames } from "../BPPanelSettings/utils";

export type TMissingInfos = {
    account?: string[];
    tax?: ICustomField[];
};
interface NavBarSettingsProps {
    page: string;
    /**
     * Custom fields existing on this company:
     */
    companyBenefCustomFields: ICustomField[] | undefined;
    /**
     * Custom fields data that exist on company with the benef info:
     */
    customFieldsData: { beneficiaryId: number; customFieldTypeId: number; value: "" }[] | undefined;
    /**
     * Verify if the author got invited to complete their profile
     */
    isInvited: boolean;
    /**
     * Company settings "uses_agreements_only"
     */
    usesAgreementsOnly: boolean;
}

export const BPNavigationBarSettings = ({
    page,
    companyBenefCustomFields,
    customFieldsData,
    isInvited,
    usesAgreementsOnly,
}: NavBarSettingsProps) => {
    const [t] = useTranslation();
    const { id } = useParams();
    const { isMobile } = useIsMobile();
    const dispatch = useAppDispatch();

    const { beneficiarCompagnies, missingData } = useSelector((state: RootState) => state.beneficiary);

    const taxCustomFields = useMemo(() => {
        return companyBenefCustomFields?.filter((field) => field.formLocation === "accounting");
    }, [companyBenefCustomFields]);

    const personalInfoFields = useMemo(() => {
        return companyBenefCustomFields?.filter((field) => field.formLocation === "personalInfo");
    }, [companyBenefCustomFields]);

    const emptyMandatoryTaxCustomFields = taxCustomFields?.filter(
        (field) =>
            !!field.isRequired &&
            !customFieldsData?.find((fieldData) => fieldData.customFieldTypeId === field.id)?.value,
    );

    const emptyMandatoryPersonalInfoFields = personalInfoFields?.filter(
        (field) =>
            !!field.isRequired &&
            !customFieldsData?.find((fieldData) => fieldData.customFieldTypeId === field.id)?.value,
    );

    const hasEmptyMandatoryTaxInfo = isInvited && !!emptyMandatoryTaxCustomFields?.length;
    const hasEmptyMandatoryPersonalInfo =
        isInvited && (!!emptyMandatoryPersonalInfoFields?.length || !!missingData.length);

    const missingInfos: TMissingInfos = {
        account: getMissingFieldsNames(emptyMandatoryPersonalInfoFields, missingData),
        tax: emptyMandatoryTaxCustomFields,
    };

    return (
        <div className="bp-settings-navbar-wrapper">
            <div className="bp-settings-navbar">
                <div className="bp-settings-title-wrapper">
                    <h2>{t("NavigationBar.PersonalInfo")}</h2>
                    {isMobile && (
                        <>
                            <span className="bp-settings-navbar-subtitle">{t("BPSettings.ManageInformations")}</span>
                            {(hasEmptyMandatoryPersonalInfo || hasEmptyMandatoryTaxInfo) && (
                                <MissingInfosWarning missingInfos={missingInfos} from="settings-nav" />
                            )}
                        </>
                    )}
                </div>
                <ul className="bp-settings-navbar-list">
                    <li>
                        <div className="bp-settings-navbar-list-company">
                            <h3>{t("Settings.General")}</h3>
                        </div>
                        <Link
                            className={`bp-settings-navbar-link ${page === "profile" && "active"}`}
                            onClick={() => dispatch(toggleSettingsTab(true))}
                            to={`/settings/profile`}
                        >
                            {t("NavigationBar.Settings")}
                        </Link>
                    </li>
                    {beneficiarCompagnies.map((elm, index) => {
                        return (
                            <li key={index}>
                                <div className="bp-settings-navbar-list-company">
                                    {elm.avatarUrl ? (
                                        <img
                                            className="bp-settings-navbar-list-company-logo"
                                            src={elm.avatarUrl}
                                            alt={elm.name}
                                        />
                                    ) : (
                                        <ApartmentOutlined className="bp-settings-navbar-list-company-logo" />
                                    )}
                                    <h3>{elm.name}</h3>
                                </div>
                                <Link
                                    className={`bp-settings-navbar-link${
                                        page === "account" && +id! === elm.id ? " active" : ""
                                    }`}
                                    onClick={() => dispatch(toggleSettingsTab(true))}
                                    to={`/settings/account/${elm.id}`}
                                >
                                    {t("Settings.Account")}
                                    {hasEmptyMandatoryPersonalInfo && (
                                        <Tooltip title={t("BeneficiarInfo.IncompleteInformations")}>
                                            <ReportProblemOutlinedIcon
                                                sx={{
                                                    height: "14px",
                                                    width: "14px",
                                                    marginLeft: "0.5rem",
                                                    color: "var(--danger)",
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Link>
                                {!usesAgreementsOnly &&
                                    (!elm.countryFeatures ||
                                        elm.countryFeatures.precount.options.length > 0 ||
                                        elm.countryFeatures.vat.options.length > 0 ||
                                        elm.countryFeatures.precountMode === "social_profile_type") && (
                                        <Link
                                            className={`bp-settings-navbar-link${
                                                page === "tax" && +id! === elm.id ? " active" : ""
                                            }`}
                                            onClick={() => dispatch(toggleSettingsTab(true))}
                                            to={`/settings/tax/${elm.id}`}
                                        >
                                            {t("Settings.TaxProfile")}
                                            {hasEmptyMandatoryTaxInfo && (
                                                <Tooltip title={t("BeneficiarInfo.IncompleteInformations")}>
                                                    <ReportProblemOutlinedIcon
                                                        sx={{
                                                            height: "14px",
                                                            width: "14px",
                                                            marginLeft: "0.5rem",
                                                            color: "var(--danger)",
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Link>
                                    )}
                                {!usesAgreementsOnly && (
                                    <Link
                                        className={`bp-settings-navbar-link${
                                            page === "payment" && +id! === elm.id ? " active" : ""
                                        }`}
                                        onClick={() => dispatch(toggleSettingsTab(true))}
                                        to={`/settings/payment/${elm.id}`}
                                    >
                                        {t("Settings.PaymentMethod")}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

export default BPNavigationBarSettings;
